import ReactGantt, { ReactGanttRef } from '@dhx/react-gantt';
import '@dhx/react-gantt/dist/react-gantt.css';
import { Typography } from '@mui/material';
import escapeHTML from 'escape-html';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useIntl } from 'react-intl';

import { differenceInCalendarDays, endOfDay, parseISO } from 'date-fns';
import { DocumentType, PersonReadDTO } from '../../api/dto';
import { EmptyState, MissingStateReasonEnum } from '../../components/searching/EmptyState';
import { useDeviceType } from '../../hooks/useDeviceType';
import { getRoute, ROUTES } from '../../routes/routes';
import './calendar.css';
import { getSafetyDocumentColor } from './ColorMapping';
import { CalendarProps, GanttDocument, RGB } from './types';

const Calendar: React.FC<CalendarProps> = ({ activities, zoom, startDate, endDate, dateFormat }) => {
    const ganttRef = useRef<ReactGanttRef>(null);
    const [formatDate, setFormatDate] = useState<Function>();
    const isMobile: boolean = useDeviceType().isMobile;
    const intl = useIntl();

    const clearDocumentId = (id: string) => {
        return id.substring(id.indexOf('-') + 1);
    };

    const activitiesWithSafetyDocuments = useMemo(() => {
        return activities.data.flatMap((activity) => {
            const { id: parent, start_date: activityStartDate, end_date: activityEndDate } = activity;

            const safetyDocuments = [
                { type: DocumentType.NDC, items: activity.notesDeCoupure },
                { type: DocumentType.IS37, items: activity.is37s },
                { type: DocumentType.VIC, items: activity.vics },
                { type: DocumentType.LOK, items: activity.lockouts },
                { type: DocumentType.FP, items: activity.firePermits },
            ];

            if (activity.rpAssessment?.dimrVersion) {
                Object.assign(safetyDocuments, {
                    type: DocumentType.DIMR,
                    items: [activity.rpAssessment.dimrVersion],
                });
            }

            const getStartAndEndDate = (start: string | null, end: string | null) => {
                if (start && end) {
                    return {
                        start: parseISO(start),
                        end: endOfDay(parseISO(end)),
                    };
                }

                return {
                    start: activityStartDate,
                    end: activityEndDate,
                };
            };

            const getSectionLabel = (responsible?: PersonReadDTO) => {
                const orgUnitCode = responsible?.orgUnitCode ?? '';
                return orgUnitCode.includes('-') ? orgUnitCode.slice(0, orgUnitCode.lastIndexOf('-')) : 'DEFAULT';
            };

            const getResponsibleLabel = (responsible?: PersonReadDTO) => {
                const fullName = responsible?.fullName ?? '';
                return fullName.includes('(')
                    ? fullName.slice(0, fullName.indexOf('(')).trim()
                    : fullName.trim() || 'responsible';
            };

            const getDocumentProps = (type: DocumentType, item: any) => {
                switch (type) {
                    case DocumentType.NDC:
                        return {
                            text: `NDC ${item.id}`,
                            start: item.startDate,
                            end: item.endDate,
                            responsible: getResponsibleLabel(item.createdBy),
                            section: getSectionLabel(item.createdBy),
                        };

                    case DocumentType.IS37:
                        return {
                            text: `IS37 ${item.id}`,
                            start: item.startDate,
                            end: item.endDate,
                            responsible: getResponsibleLabel(item.responsible),
                            section: getSectionLabel(item.responsible),
                        };

                    case DocumentType.VIC:
                        return {
                            text: `VIC ${item.id}`,
                            start: item.scheduledStartDate,
                            end: item.scheduledEndDate,
                            responsible: getResponsibleLabel(item.createdBy),
                            section: getSectionLabel(item.createdBy),
                        };

                    case DocumentType.LOK:
                        return {
                            text: `Lockout ${item.id}`,
                            start: item.requestStartDate,
                            end: item.requestEndDate,
                            responsible: getResponsibleLabel(item.createdBy),
                            section: getSectionLabel(item.createdBy),
                        };

                    case DocumentType.FP:
                        return {
                            text: `Fire Permit ${item.id}`,
                            start: item.startDate,
                            end: item.endDate,
                            responsible: getResponsibleLabel(item.responsible),
                            section: getSectionLabel(item.responsible),
                        };

                    case DocumentType.DIMR:
                        return {
                            text: `DIMR ${item.id}`,
                            description: item.description,
                            start: item.workStartDate,
                            end: item.workEndDate,
                            responsible: getResponsibleLabel(item.createdBy),
                            section: getSectionLabel(item.createdBy),
                        };

                    default:
                        return {
                            text: `Document ${item.id}`,
                            start: activityStartDate,
                            end: activityEndDate,
                            responsible: getResponsibleLabel(item.createdBy),
                            section: getSectionLabel(item.createdBy),
                        };
                }
            };

            const children = safetyDocuments.flatMap(({ type, items }) =>
                items
                    .map((item) => {
                        const props = getDocumentProps(type, item);
                        const { start, end } = getStartAndEndDate(props.start, props.end);
                        const duration = differenceInCalendarDays(start, end) + 1;

                        // Necessary to handle long-lasting behavior
                        if (start > endDate || end < startDate) {
                            return null;
                        }

                        return {
                            // All the safety documents are merged with the activities, this is necessary to avoid duplicated IDs
                            id: `${type}-${item.id}`,
                            parent,
                            type,
                            text: props.text,
                            description: props.description,
                            responsible: props.responsible,
                            section: props.section,
                            color: getSafetyDocumentColor(type),
                            start_date: start,
                            end_date: end,
                            duration,
                            notesDeCoupure: [],
                            is37s: [],
                            vics: [],
                            lockouts: [],
                            firePermits: [],
                            rpAssessment: null,
                        };
                    })
                    .filter(Boolean)
            );

            return [activity, ...children];
        });
    }, [activities.data]);

    const GanttTooltip: React.FC<{ document: GanttDocument }> = ({ document }) => {
        return (
            <>
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>ID:</strong> {clearDocumentId(document.id)}
                </Typography>
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>Title:</strong> {document.text}
                </Typography>
                {document.description && (
                    <Typography className="gantt_tooltip_text" variant="body2">
                        <strong>Description:</strong> {document.description}
                    </Typography>
                )}
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>Responsible:</strong> {document.responsible}
                </Typography>
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>Start:</strong> {formatDate && formatDate(document.start_date)}
                </Typography>
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>End:</strong> {formatDate && formatDate(document.end_date)}
                </Typography>
                <Typography className="gantt_tooltip_text" variant="body2">
                    <strong>Duration:</strong> {document.duration}
                </Typography>
            </>
        );
    };

    // This function was based in this answer from StackOverflow: https://stackoverflow.com/a/9733420
    const calculateContrastRatio = useCallback((rgb1: RGB, rgb2: RGB) => {
        // These numbers are the "Relative Luminance" of each color, they can be obtained in this website: https://www.siegemedia.com/contrast-ratio
        const RED = 0.2126;
        const GREEN = 0.7152;
        const BLUE = 0.0722;

        const GAMMA = 2.4;

        function luminance({ r, g, b }: RGB): number {
            const luminanceValues = [r, g, b].map((value) => {
                const normalizedValue = value / 255;

                // Use linear scaling for dark colors or gamma correction for the lighter
                return normalizedValue <= 0.03928
                    ? normalizedValue / 12.92
                    : Math.pow((normalizedValue + 0.055) / 1.055, GAMMA);
            });

            return luminanceValues[0] * RED + luminanceValues[1] * GREEN + luminanceValues[2] * BLUE;
        }

        const lum1 = luminance(rgb1);
        const lum2 = luminance(rgb2);

        const brightest = Math.max(lum1, lum2);
        const darkest = Math.min(lum1, lum2);

        // The 0.05 is to ensure that the ratio is above 1
        return (brightest + 0.05) / (darkest + 0.05);
    }, []);

    const getDocumentTextClass = (document: GanttDocument) => {
        const rgbArray = document.color?.match(/\d+/g);

        // 21 = max contrast possible
        let blackContrast = 21;
        let whiteContrast = 21;
        if (rgbArray && rgbArray.length === 3) {
            const rgbObject = {
                r: Number(rgbArray[0]),
                g: Number(rgbArray[1]),
                b: Number(rgbArray[2]),
            };
            const black = {
                r: 0,
                g: 0,
                b: 0,
            };
            const white = {
                r: 255,
                g: 255,
                b: 255,
            };
            blackContrast = calculateContrastRatio(rgbObject, black);
            whiteContrast = calculateContrastRatio(rgbObject, white);
        }

        return `gantt_task_details ${
            blackContrast > whiteContrast ? 'gantt_task_details_black' : 'gantt_task_details_white'
        }`;
    };

    const GanttText: React.FC<{ document: GanttDocument }> = ({ document }) => {
        return (
            <Typography variant="body2" className={`${getDocumentTextClass(document)}`}>
                {escapeHTML(clearDocumentId(document.id))}
            </Typography>
        );
    };

    const handleDocumentClick = (id: string, e?: Event) => {
        const document = activitiesWithSafetyDocuments.find((a) => a && a.id == id);
        if (!document) return false;

        const gantt = ganttRef.current?.instance;
        if (!gantt) return false;

        const taskRowNode = gantt.getTaskRowNode(id);
        if (e?.target && taskRowNode && taskRowNode.contains(e.target as Node)) {
            return true;
        }

        gantt.ext.tooltips.tooltip.hide();

        const route = {
            [DocumentType.ACTIVITY]: ROUTES.activity.view,
            [DocumentType.NDC]: ROUTES.noteDeCoupure.view,
            [DocumentType.IS37]: ROUTES.is37.view,
            [DocumentType.VIC]: ROUTES.vic.view,
            [DocumentType.LOK]: ROUTES.lockout.view,
            [DocumentType.FP]: ROUTES.firePermit.view,
            [DocumentType.DIMR]: ROUTES.dimr.view,

            // will not be shown, they're here because of the Typescript
            [DocumentType.IS37_CR]: ROUTES.activity.view,
            [DocumentType.RPA]: ROUTES.activity.view,
            [DocumentType.WDP]: ROUTES.activity.view,
            [DocumentType.ACTIVITY_CR]: ROUTES.activity.view,
        };

        window.open(getRoute({ path: route[document.type], params: { id: clearDocumentId(id) } }));

        // Typescript complains without this return
        return true;
    };

    useEffect(() => {
        const gantt = ganttRef.current?.instance;
        if (!gantt) return;

        setFormatDate(() => gantt.date.date_to_str(dateFormat));

        gantt.ext.zoom.init({
            levels: [
                {
                    name: 'Days',
                    scale_height: 60,
                    min_column_width: 55,
                    scales: [
                        { unit: 'week', step: 1, format: '%M %Y' },
                        { unit: 'day', step: 1, format: '%D %d' },
                    ],
                },
                {
                    name: 'Months',
                    scale_height: 60,
                    min_column_width: 45,
                    scales: [
                        { unit: 'month', step: 1, format: '%F %Y' },
                        {
                            unit: 'week',
                            step: 1,
                            format: (date) => {
                                const weekStart = gantt.date.week_start(new Date(date));
                                const weekEnd = gantt.date.add(weekStart, 6, 'day');
                                const formatDay = gantt.date.date_to_str('%d');
                                return `${formatDay(weekStart)}-${formatDay(weekEnd)}`;
                            },
                        },
                    ],
                },
            ],
        });
    }, []);

    useEffect(() => {
        const gantt = ganttRef.current?.instance;
        if (!gantt) return;

        gantt.ext.zoom.setLevel(zoom);
        gantt.render();
    }, [zoom]);

    const [event, setEvent] = useState<string | null>(null);

    // Attaching the onTaskClick event directly to the <ReactGantt> component causes issues when loading additional pages
    useEffect(() => {
        const gantt = ganttRef.current?.instance;
        if (!gantt) return;

        if (event) gantt.detachEvent(event);
        const eventId = gantt.attachEvent('onTaskClick', (id: string, e?: Event) => handleDocumentClick(id, e));
        setEvent(eventId);
    }, [activitiesWithSafetyDocuments]);

    if (activities.data.length === 0) {
        return (
            <EmptyState
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                reason={MissingStateReasonEnum.EMPTY}
                message={intl.formatMessage({ id: 'calendar.noActivity' })}
            />
        );
    }

    return (
        <ReactGantt
            ref={ganttRef}
            tasks={activitiesWithSafetyDocuments}
            config={{
                touch: true,
                row_height: 25,
                start_date: startDate,
                end_date: endDate,
                columns: [
                    {
                        name: 'title',
                        label: 'Title',
                        tree: true,
                        align: 'left',
                        min_width: isMobile ? 120 : 400,
                        max_width: isMobile ? 200 : 800,
                        template: (document: GanttDocument) => escapeHTML(document.text),
                    },
                ],
                date_format: dateFormat,
                readonly: true,
            }}
            plugins={{
                tooltip: true,
            }}
            templates={{
                task_class: (start: Date, end: Date, document: GanttDocument) => {
                    if (document.showArrow === false) {
                        return 'gantt_hide_arrow';
                    }
                    return '';
                },
                task_text: (start: Date, end: Date, document: GanttDocument) => {
                    return renderToStaticMarkup(<GanttText document={document} />);
                },
                tooltip_text: (start: Date, end: Date, document: GanttDocument) => {
                    return renderToStaticMarkup(<GanttTooltip document={document} />);
                },
            }}
        />
    );
};

export { Calendar };
